export type FanRafflePhase = 'idle' | 'selected' | 'disposing' | 'gone';

export const FAN_RAFFLE_COPY = {
  entryTitle: 'Sign the back!',
  entrySub: 'Your signature seals your entry.\nOne ticket per fan.',
  signHere: 'Sign here',
  submittedPill: 'Entry locked',
  submittedTitle: "You're in!",
  submittedSub: 'Keep your fingers crossed!',
  readyPill: 'Entries closed',
  readyTitle: 'Drawing soon',
  readySub: 'Entries closed — hang tight while the host starts the draw.',
  stillInPill: 'Still in',
  stillInTitle: 'Still in!',
  stillInSub: 'Hang on — tickets are being eliminated one by one.',
  stillInLabel: 'Still in!',
  eliminatedPill: 'Eliminated',
  eliminatedTitle: 'Out!',
  eliminatedSub: 'Your ticket was drawn — better luck next raffle.',
  winnerPill: 'You won',
  winnerTitle: 'Winner!',
  winnerSub: 'Your ticket made it through the draw.',
  loserPill: 'Out',
  loserTitle: 'Better luck next time',
  loserSub: "This draw's over — hang tight for the next one.",
} as const;

/** 1-based place in `raffleCandidates` (user ids, entry order). */
export function raffleFanEntryNumber(
  candidates: readonly string[] | null | undefined,
  userId: string | null | undefined,
): number | null {
  if (!userId || !candidates?.length) {
    return null;
  }
  const index = candidates.indexOf(userId);
  return index >= 0 ? index + 1 : null;
}

export function raffleFanTicketMeta({
  status,
  serial,
  entryNumber,
  drawingStillIn,
  drawingEliminated,
}: {
  status?: string | null;
  serial: string | null;
  entryNumber: number | null;
  drawingStillIn: boolean;
  drawingEliminated: boolean;
}): string | null {
  if (!serial) {
    return null;
  }
  const entry = entryNumber != null ? `#${entryNumber}` : null;
  if (status === 'OPEN') {
    return entry ? `Entry ${entry} · Ticket ${serial}` : `Ticket ${serial}`;
  }
  if (status === 'READY_TO_DRAW') {
    return entry
      ? `Your ticket · ${serial} · Entry ${entry}`
      : `Your ticket · ${serial}`;
  }
  if (drawingStillIn || drawingEliminated) {
    return `Ticket ${serial}`;
  }
  return null;
}

/** Matches live Fan: non-production, ticket still on screen, not completed. */
export function shouldShowFanRaffleLogout({
  isProduction,
  status,
  hasSubmission,
  phase,
}: {
  isProduction: boolean;
  status?: string | null;
  hasSubmission: boolean;
  phase: FanRafflePhase;
}): boolean {
  return (
    !isProduction && status !== 'COMPLETED' && hasSubmission && phase !== 'gone'
  );
}

export function shouldShowFanRaffleTicket({
  hasSubmission,
  phase,
  status,
  stillIn,
}: {
  hasSubmission: boolean;
  phase: FanRafflePhase;
  status?: string | null;
  stillIn: boolean;
}): boolean {
  if (!hasSubmission) {
    return false;
  }
  if (phase !== 'gone') {
    return true;
  }
  if (status === 'COMPLETED') {
    return true;
  }
  return status === 'DRAWING' && !stillIn;
}
